import { FC, PropsWithChildren } from "react";
import {
    AppBar,
    Box,
    IconButton,
    Toolbar,
    Typography,
    useTheme as useMuiTheme,
    useMediaQuery
} from "@mui/material";
import tintoaLogoWhite from "../img/logo_tintoa_white.png";
import tintoaLogoBlack from "../img/logo_tintoa_black.png";
import { Brightness4, Brightness7 } from "@mui/icons-material";
import { TintoaIdentity, TintoaPageBackground } from "@tintoa/ui";
import { useNavigate } from 'react-router-dom';
import { useTheme as useAppTheme } from '../theme/ThemeContext';

export const InAppContainer: FC<PropsWithChildren> = ({ children }) => {
    const navigate = useNavigate();
    const { toggleColorMode } = useAppTheme();
    const muiTheme = useMuiTheme();
    const isDarkTheme = muiTheme.palette.mode === 'dark';
    const isLargeScreen = useMediaQuery(muiTheme.breakpoints.up('lg'));

    return (
        <Box 
            sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                minHeight: '100vh',
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            {/* Background with blur effect for large screens */}
            <TintoaPageBackground position="absolute" largeScreenOnly />

            <AppBar 
                position="static" 
                color="default" 
                elevation={0}
                sx={{
                    bgcolor: muiTheme.palette.overlay!.subtle,
                    borderBottom: 1,
                    borderColor: 'divider',
                    position: 'relative',
                    zIndex: 1,
                    transition: 'all 0.3s ease-in-out'
                }}
            >
                <Toolbar sx={{ px: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <img
                            height={28}
                            src={isDarkTheme ? tintoaLogoWhite : tintoaLogoBlack}
                            alt="tintoa-logo"
                            style={{ display: 'block', objectFit: 'contain' }}
                        />
                        <Typography 
                            variant="h6" 
                            component="div" 
                            sx={{ 
                                cursor: 'pointer',
                                color: 'text.primary',
                                fontWeight: 500
                            }} 
                            onClick={() => navigate('/')}
                        >
                            Kontoverwaltung
                        </Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton
                            onClick={() => toggleColorMode()}
                            color="inherit"
                            sx={{ 
                                color: 'text.secondary',
                                '&:hover': { 
                                    bgcolor: isLargeScreen 
                                        ? muiTheme.palette.overlay!.hover
                                        : 'action.hover'
                                }
                            }}
                        >
                            {isDarkTheme ? <Brightness7 /> : <Brightness4 />}
                        </IconButton>
                        <TintoaIdentity
                            onLogout={() => window.location.replace('/')}
                        />
                    </Box>
                </Toolbar>
            </AppBar>
            <Box 
                component="main" 
                sx={{ 
                    flexGrow: 1,
                    position: 'relative',
                    zIndex: 1
                }}
            >
                {children}
            </Box>
        </Box>
    );
};
